/**
 * MethodBlock — Matching composition block (§9.2).
 *
 * Block M1a-FE (TZ 2026-07-29). НЕ три mutually exclusive screens:
 *   - 0..1 base template
 *   - 0..N manual properties
 *
 * Размещается под ProcessControlBar. Disabled пока `!datasetId`.
 *
 * Language picker lives HERE for matching per §5.3 — ProcessControlBar slot 7
 * is the generic 10-language list used by Search/Generation (we keep PCB
 * untouched and ProcessMatchingPage supplies `setOutputLanguage=undefined`).
 * Per §5.3 rules:
 *   - template from global/marketplace list → UA|RU editable picker.
 *   - template from user list → fixed language (display only).
 *   - manual-only (no template) → picker hidden.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet, Link2, Plus, Settings2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ManualPropertyCard } from './ManualPropertyCard';
import { useMatchingStore } from '@/store/matchingStore';
import { useMasterTemplates } from '@/hooks/useAdmin';
import { cn } from '@/lib/utils';

export interface MethodBlockProps {
  datasetId: string | null;
}

export function MethodBlock({ datasetId }: MethodBlockProps) {
  const { t } = useTranslation();
  const templateId = useMatchingStore((s) => s.templateId);
  const templateScope = useMatchingStore((s) => s.templateScope);
  const setTemplate = useMatchingStore((s) => s.setTemplate);
  const language = useMatchingStore((s) => s.language);
  const setLanguage = useMatchingStore((s) => s.setLanguage);
  const manualProperties = useMatchingStore((s) => s.manualProperties);
  const addManualProperty = useMatchingStore((s) => s.addManualProperty);
  const updateManualProperty = useMatchingStore((s) => s.updateManualProperty);
  const removeManualProperty = useMatchingStore((s) => s.removeManualProperty);

  const { data: globalTemplates } = useMasterTemplates('global');
  const { data: userTemplates } = useMasterTemplates('user');
  // Annotate each template with its scope so onChange can persist it.
  const readyTemplates = useMemo(() => {
    const globalList = (globalTemplates ?? [])
      .filter((mt) => mt.status === 'ready')
      .map((mt) => ({ ...mt, _scope: 'global' as const }));
    const userList = (userTemplates ?? [])
      .filter((mt) => mt.status === 'ready')
      .map((mt) => ({ ...mt, _scope: 'user' as const }));
    return [...globalList, ...userList];
  }, [globalTemplates, userTemplates]);

  const disabled = !datasetId;
  const atCardLimit = manualProperties.length >= 8;
  const isMarketplace = templateScope === 'global';
  const isUserTemplate = templateScope === 'user' && Boolean(templateId);

  return (
    <section className="card space-y-4" data-testid="method-block">
      <header className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-surface-400 flex items-center gap-2">
          <Settings2 className="w-4 h-4" />
          {t('process.matching.composition.methodTitle', {
            defaultValue: 'Matching method',
          })}
        </h2>
      </header>

      {/* === Base template block (0..1) === */}
      <div className="space-y-2">
        <label
          htmlFor="template-picker"
          className="text-xs uppercase tracking-wider text-surface-500 flex items-center gap-1.5"
        >
          <FileSpreadsheet className="w-3.5 h-3.5" />
          {t('process.matching.pickers.template', { defaultValue: 'Template' })}
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <select
            id="template-picker"
            className="input py-1 text-sm flex-1 min-w-[16rem] max-w-md disabled:opacity-60"
            value={templateId ?? ''}
            onChange={(e) => {
              const id = e.target.value || null;
              if (!id) {
                setTemplate(null, null);
                return;
              }
              const found = readyTemplates.find((mt) => mt.id === id);
              setTemplate(id, found?._scope ?? null);
            }}
            disabled={disabled}
            aria-label={t('process.matching.pickers.template', {
              defaultValue: 'Template',
            })}
          >
            <option value="">
              {t('process.matching.composition.withoutTemplate', {
                defaultValue: 'Without template (manual-only)',
              })}
            </option>
            {readyTemplates.map((mt) => (
              <option key={mt.id} value={mt.id}>
                {mt.display_name}
                {mt._scope === 'global'
                  ? t('process.matching.composition.templateBadgeMarketplace', {
                      defaultValue: ' · marketplace',
                    })
                  : t('process.matching.composition.templateBadgeUser', {
                      defaultValue: ' · yours',
                    })}
              </option>
            ))}
          </select>

          {/* UA|RU marketplace picker — editable only for global/marketplace
              templates. PCB slot 7 is suppressed by ProcessMatchingPage for
              Matching (setOutputLanguage=undefined) so this is the only
              language picker visible here per §5.3. */}
          {isMarketplace && (
            <select
              aria-label={t('process.matching.pickers.language', {
                defaultValue: 'Language',
              })}
              className="input py-1 text-sm w-32"
              value={language}
              onChange={(e) => setLanguage(e.target.value as 'ua' | 'ru')}
              data-testid="language-picker-marketplace"
            >
              <option value="ua">UA</option>
              <option value="ru">RU</option>
            </select>
          )}

          {/* User-template: language is fixed by the template's own canonical
              names, no editable picker. Display language used as plaintext. */}
          {isUserTemplate && (
            <span
              className="text-[11px] text-surface-500"
              data-testid="language-fixed-user"
            >
              {t('process.matching.composition.languageFixed', {
                defaultValue: 'Language: fixed by your template ({lang})',
                lang: language.toUpperCase(),
              })}
            </span>
          )}
        </div>

        {/* Upload own link */}
        <p className="text-xs text-surface-500 flex items-center gap-1.5">
          <Link2 className="w-3 h-3" />
          <Link
            to="/settings/system"
            className="text-primary-300 hover:text-primary-200 underline"
          >
            {t('process.matching.composition.uploadOwn', {
              defaultValue: 'Upload your template at /settings/system',
            })}
          </Link>
        </p>

        {disabled && (
          <p className="text-xs text-warning">
            {t('process.matching.pickers.datasetLocked', {
              defaultValue: 'pick a project first',
            })}
          </p>
        )}
      </div>

      {/* === Manual properties (0..N) === */}
      <div className="space-y-2 pt-2 border-t border-surface-800">
        <div className="flex items-center justify-between">
          <h3 className="text-xs uppercase tracking-wider text-surface-500">
            {t('process.matching.composition.manualProperties', {
              defaultValue: 'Manual properties',
            })}
          </h3>
          <button
            type="button"
            onClick={addManualProperty}
            disabled={disabled || atCardLimit}
            className={cn(
              'flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors',
              disabled || atCardLimit
                ? 'bg-surface-800 text-surface-500 cursor-not-allowed'
                : 'bg-primary-600 hover:bg-primary-500 text-white',
            )}
          >
            <Plus className="w-3 h-3" />
            {t('process.matching.composition.addProperty', {
              defaultValue: 'Add property',
            })}
          </button>
        </div>

        {manualProperties.length === 0 && templateId ? (
          <p className="text-xs text-surface-500 italic">
            {t('process.matching.composition.manualProperties', {
              defaultValue: 'Manual properties (optional)',
            })}
          </p>
        ) : manualProperties.length === 0 ? (
          <p className="text-xs text-surface-500 italic">
            {t('process.matching.manualOnlyHint', {
              defaultValue:
                'Manual-only mode: categories skipped, only manual properties (M1b).',
            })}
          </p>
        ) : (
          <div
            className={cn(
              'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2',
              disabled && 'opacity-60 pointer-events-none',
            )}
          >
            {manualProperties.map((p, i) => (
              <ManualPropertyCard
                key={p.id}
                index={i}
                value={p}
                onChange={(patch) => updateManualProperty(p.id, patch)}
                onRemove={() => removeManualProperty(p.id)}
              />
            ))}
          </div>
        )}
        {atCardLimit && (
          <p className="text-[11px] text-warning/80">
            {t('process.matching.composition.cardLimitHint', {
              defaultValue: 'Up to 8 cards.',
            })}
          </p>
        )}
      </div>
    </section>
  );
}
